import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import "./ImageCard.scss";

import { Skeleton } from "primereact/skeleton";
import { classNames } from "primereact/utils";

import { faDownload } from "@fortawesome/pro-regular-svg-icons";

import { ActionButton } from "../../../components/Shared";
import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";

const imagePlaceholderPath = "/src/assets/images/image-generation/image-placeholder.svg";
const featureTranslationKeys = translationKeys.feature.imageGeneration;

interface IImageCardProps {
  imageCount: number;
  loading: boolean;
  disabled: boolean;
  error: boolean;
  objectId?: string;
  blob?: Blob;
  isSelected?: boolean;
  onSelect?: () => void;
  handleDownload?: (imageId?: string) => void;
  disableActions?: boolean;
}

export const ImageCard: FunctionComponent<IImageCardProps> = ({
  imageCount,
  loading,
  disabled,
  error,
  objectId,
  blob,
  isSelected,
  onSelect,
  handleDownload,
}) => {
  const { t } = useTranslation();

  return (
    <div className="image-card" role="group" aria-label={`Image ${objectId}`}>
      <div className="image-toolbar" aria-hidden={false}>
        <ActionButton
          id={objectId ? `image-card-download-button-${objectId}` : "image-card-download-button"}
          hide={error || disabled || loading}
          icon={faDownload}
          hideBackground={true}
          tooltip={t(featureTranslationKeys.actions.downloadImage)}
          tooltipPosition="bottom"
          buttonDisabled={loading || disabled}
          onClick={e => {
            if (!handleDownload || !blob || disabled) {
              return;
            }

            e.stopPropagation();
            handleDownload(objectId);
          }}
        />
      </div>

      <TooltipWrapper
        targetRef=".image-thumb"
        content={imageCount <= 1 ? "" : t(featureTranslationKeys.actions.selectImage)}
        position="top"
        disabled={loading || disabled}
      />

      <div
        className={classNames("image-thumb", {
          disabled: loading || disabled,
          selected: isSelected,
          "single-image": imageCount <= 1,
        })}
        data-testid="image-thumb"
        onClick={disabled ? undefined : onSelect}
      >
        {loading ? (
          <div className="thumb-skeleton" data-testid="image-thumb-skeleton">
            <Skeleton width="100%" height="100%" />
          </div>
        ) : error || !blob ? (
          <img src={imagePlaceholderPath} alt="placeholder" data-testid="image-thumb-placeholder" />
        ) : (
          <img
            src={URL.createObjectURL(blob)}
            alt="AI generated image"
            data-testid={`image-thumb-generated-${objectId}`}
            onError={e => {
              (e.currentTarget as HTMLImageElement).src = imagePlaceholderPath;
            }}
            loading="lazy"
          />
        )}
      </div>
    </div>
  );
};
